import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createEmployee,
  getAllEmployee,
  deleteEmployee,
  updateEmployee,
  toggleEmployeeStatus,
} from "../../State/Employee/Action";
import { getAllPinCodeArea } from "../../State/PinCodeArea/Action";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Card,
  CardHeader,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  CardContent,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import { FileDownload, PictureAsPdf, Print, Add as AddIcon, Edit, Delete, UploadFile } from '@mui/icons-material';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { PermissionService } from "../../../config/permissionService";
import { getSelectedExportRows } from "../../../config/exportSelection";
import ImagePopup from "../../../Common/ImagePopup";

const AddEmployee = () => {
  const dispatch = useDispatch();
  const { employees, pincodeAreas } = useSelector((store) => store);
  const [showPassword, setShowPassword] = useState(false);

  const [employeeData, setEmployeeData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    fullAddress: "",
    aadhaarNumber: ""
  });
  const [editingId, setEditingId] = useState(null);
  const [selectedMasterPinCodeId, setSelectedMasterPinCodeId] = useState(0);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [error, setError] = useState("");
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const fileInputRef = React.useRef(null);
  const [imageOpen, setImageOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [frontPreview, setFrontPreview] = useState(null);
  const [backPreview, setBackPreview] = useState(null);
  const [frontError, setFrontError] = useState("");
  const [backError, setBackError] = useState("");
  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [frontUploadedImage, setFrontUploadedImage] = useState(null);
  const [backUploadedImage, setBackUploadedImage] = useState(null);

  useEffect(() => {
    dispatch(getAllEmployee());
    dispatch(getAllPinCodeArea());
    setShowPassword(false)
  }, [dispatch, openModal]);


  const handleChange = (e) => {
    const { name, value } = e.target;

    // Allow only digits in phone
    if (name === "phone" && !/^\d*$/.test(value)) return;
    if (name === "aadhaarNumber" && !/^\d*$/.test(value)) return;


    // Validate email
    if (name === "email" && value && !/^\S+@\S+\.\S+$/.test(value)) {
      setError("Invalid email");
    } else {
      setError("");
    }
    setEmployeeData((prev) => ({ ...prev, [name]: value }));
  };


  const handleFrontFileChange = (e) => {
    const file = e.target.files[0];
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (file && allowedTypes.includes(file.type)) {
      // ✅ File size check (max 2MB)
      if (file?.size > 2 * 1024 * 1024) {
        setFrontError("Only less then 2MB size image are allowed!");
        setFrontPreview(null);
        setFrontFile(null);
        return;
      }
      // ✅ Quality check
      setFrontPreview(URL.createObjectURL(file));
      setFrontFile(file);
      setFrontError("");
      checkImageQuality(file, "front");
    } else {
      setFrontError("Please upload a valid image");
      setFrontPreview(null)
      setFrontFile(null);
    }
  };

  const handleBackFileChange = (e) => {
    const file = e.target.files[0];
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (file && allowedTypes.includes(file.type)) {
      // ✅ File size check (max 2MB)
      if (file?.size > 2 * 1024 * 1024) {
        setBackError("Only less then 2MB size image are allowed!");
        setBackPreview(null);
        setBackFile(null);
        return;
      }
      // ✅ Quality check
      setBackPreview(URL.createObjectURL(file));
      setBackFile(file);
      setBackError("");
      checkImageQuality(file, "back");
    } else {
      setBackError("Please upload a valid image");
      setBackPreview(null)
      setBackFile(null);
    }

  };

  const checkImageQuality = (file, type) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);

    img.onload = () => {
      const width = img.width;
      const height = img.height;

      // ✅ Blur detection (contrast-based sharpness check)
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);

      const imageData = ctx.getImageData(0, 0, width, height);
      const pixels = imageData.data;

      let diff = 0;

      for (let i = 0; i < pixels.length; i += 4) {
        const brightness = (pixels[i] + pixels[i + 1] + pixels[i + 2]) / 3;

        if (i >= 4) {
          const prevBrightness =
            (pixels[i - 4] + pixels[i - 3] + pixels[i - 2]) / 3;
          diff += Math.abs(brightness - prevBrightness);
        }
      }

      const contrast = diff / (pixels.length / 4);

      // ✅ Blur detection threshold (agar contrast bohot kam hai = blurry)
      if (contrast < 2) {
        if (type === "back") {
          setBackError("Blur image are not allowed!");
          setBackPreview(null)
        } else {
          setFrontError("Blur image are not allowed!");
          setFrontPreview(null)
        }
        return;
      }

      if (type === "back") {
        setBackPreview(URL.createObjectURL(file))
        setBackFile(file);
      } else {
        setFrontPreview(URL.createObjectURL(file))
        setFrontFile(file);
      }

      // Reset the file input so same file can be uploaded again
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
    };
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!employeeData.name.trim() || !employeeData.email.trim() || !employeeData.phone.trim() || !employeeData.fullAddress.trim() || !employeeData.aadhaarNumber.trim()) {
      toast.error("Please fill all the fields");
      return;
    }
    if (selectedMasterPinCodeId === 0) {
      toast.error("Please select a PinCode Area");
      return;
    }
    if (employeeData.phone.length < 10) {
      toast.error("Employee phone should be 10 digits");
      return;
    }
    if (employeeData.aadhaarNumber.length < 12) {
      toast.error("Aadhaar Number should be 12 digits");
      return;
    }
    if (employeeData.name.length < 3 || employeeData.name.length > 20) {
      toast.error("Employee name should be between 3 to 20 characters");
      return;
    }

    if (employeeData.fullAddress.length < 10 || employeeData.fullAddress.length > 100) {
      toast.error("Address should be between 10 to 100 characters");
      return;
    }

    if (/^\S+@\S+\.\S+$/.test(employeeData.email.length)) {
      setError("Invalid email");
      return
    }

    if (error !== "") {
      return null;
    }

    const formData = new FormData();
    formData.append("frontImage", frontFile);  // Front side
    formData.append("backImage", backFile);   // Back side

    if (editingId) {
      if (employeeData.password.trim()) {
        if (employeeData.password.length < 6 || employeeData.password.length > 15) {
          toast.error("Password should be between 6 to 15 characters");
          return;
        }
      }
      dispatch(updateEmployee(editingId, selectedMasterPinCodeId, employeeData, formData, handleCloseModal, handleCancle));
    } else {
      if (!employeeData.password.trim()) {
        toast.error("password is required!!");
        return;
      }
      if (employeeData.password.length < 6 || employeeData.password.length > 15) {
        toast.error("Password should be between 6 to 15 characters");
        return;
      }
      if (!frontFile) {
        setFrontError("Please upload an Front Aadhaar image.");
        return;
      }
      if (!backFile) {
        setBackError("Please upload an Back Aadhaar image.");
        return;
      }
      dispatch(createEmployee(selectedMasterPinCodeId, employeeData, formData, handleCloseModal, handleCancle));
    }


  };

  const handleCancle = () => {
    setEmployeeData({ name: "", email: "", phone: "", password: "", fullAddress: "", aadhaarNumber: "" });
    setSelectedMasterPinCodeId(0);
    setEditingId(null);
    setFrontFile(null);
    setFrontPreview(null);
    setFrontError("");
    setBackFile(null);
    setBackPreview(null);
    setBackError("");
    setFrontUploadedImage(null)
    setBackUploadedImage(null)
  }

  const handleEmployeeUpdate = (item) => {
    setEmployeeData({
      name: item.name,
      email: item.email,
      phone: item?.phone,
      password: "",
      fullAddress: item?.fullAddress,
      aadhaarNumber: item?.aadhaarNumber
    });
    setEditingId(item.id);
    setSelectedMasterPinCodeId(item.masterPincode?.id || 0);
    setOpenModal(true);
    setFrontUploadedImage(item.aadhaarImageUrl)
    setBackUploadedImage(item.backAadhaarImageUrl)
  };

  const handleDeleteConfirmation = (id) => {
    setDeleteConfirmId(id);
  };

  const confirmDelete = () => {
    if (deleteConfirmId !== null) {
      dispatch(deleteEmployee(deleteConfirmId));
      setDeleteConfirmId(null);
    }
  };

  const handleToggleStatus = async (id, isEnabled) => {
    dispatch(toggleEmployeeStatus(id, isEnabled));
  };

  const rows = employees?.employees?.response?.map((emp, index) => ({
    id: emp?.id,
    createdAt: emp?.createdAt,
    name: emp?.name || "----",
    email: emp?.email || "----",
    phone: emp?.phone || "----",
    password: emp?.password || "----",
    fullAddress: emp?.fullAddress || "----",
    aadhaarNumber: emp?.aadhaarNumber,
    aadhaarImageUrl: emp?.aadhaarImageUrl,
    backAadhaarImageUrl: emp?.backAadhaarImageUrl,
    regionName: emp?.masterPincode?.regionName || "----",
    pincodeRange: emp?.masterPincode?.startPincode + "-" + emp?.masterPincode?.endPincode,
    masterPincode: emp?.masterPincode || "----",
    enabled: emp?.enabled,
  })) || []

  const writeAction = PermissionService.has({ category: "Admin", page: "Employees", type: "WRITE" });

  const columns = [
    {
      field: "serialNo",
      headerName: "S.No.",
      width: 80,
      sortable: false,
      renderCell: (params) => params.api.getAllRowIds().indexOf(params.id) + 1
    },
    { field: "createdAt", headerName: "Create Date", flex: 1, renderCell: (params) => params.value ? dayjs(params?.value).format("DD MMM YYYY HH:mm") : "----", },
    { field: "name", headerName: "Name", flex: 1 },
    { field: "email", headerName: "Email", flex: 1.5 },
    { field: "phone", headerName: "Phone", flex: 1 },
    { field: "fullAddress", headerName: "Address", flex: 1.5 },
    { field: "aadhaarNumber", headerName: "Aadhaar Number", flex: 1 },
    {
      field: "aadhaarImageUrl",
      headerName: "Front Aadhaar Image",
      renderCell: (params) => (
        <img onClick={() => { setImageOpen(true); setImageUrl(params?.value) }} className="cursor-pointer" src={params.value} alt="aadhaar_image" width="40" height="40" style={{ borderRadius: 8 }} />
      ),
      width: 100,
    },
    {
      field: "backAadhaarImageUrl",
      headerName: "Back Aadhaar Image",
      renderCell: (params) => (
        <img onClick={() => { setImageOpen(true); setImageUrl(params?.value) }} className="cursor-pointer" src={params.value} alt="aadhaar_image" width="40" height="40" style={{ borderRadius: 8 }} />
      ),
      width: 100,
    },
    {
      field: "regionName",
      headerName: "PinCode Area",
      flex: 1,
      valueGetter: (value) =>
        value || "----",
    },
    {
      field: "pincodeRange",
      headerName: "PinCode Range",
      flex: 1,
      valueGetter: (value) =>
        (value) || "----",
    },
    ...(writeAction ? [
      {
        field: "enabled",
        headerName: "Status",
        width: 110,
        renderCell: (params) => {
          const isEnabled = Boolean(params.value);

          return (
            // <Stack direction="row" spacing={1} alignItems="center">
            //   <Switch
            //     size="small"
            //     checked={isEnabled}
            //     onChange={() => handleToggleStatus(params.row.id, isEnabled)}
            //     sx={{
            //       "& .MuiSwitch-switchBase.Mui-checked": {
            //         color: "green",
            //       },
            //       "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
            //         backgroundColor: "green",
            //       },
            //       "& .MuiSwitch-switchBase": {
            //         color: "red",
            //       },
            //       "& .MuiSwitch-track": {
            //         backgroundColor: "red",
            //       },
            //     }}
            //   />
            // </Stack>
            <div
              onClick={() => handleToggleStatus(params.row.id, isEnabled)}
              style={{
                width: 90,
                height: 18,
                backgroundColor: isEnabled ? "#22c55e" : "#ef4444",
                display: "flex",
                alignItems: "center",
                justifyContent: isEnabled ? "flex-end" : "flex-start",
                // padding: "8px",
                cursor: "pointer",
                transition: "0.3s",
                borderRadius: 50,
                marginTop: 2,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  flexDirection: isEnabled ? "row-reverse" : "row",
                  transition: "0.3s"
                }}
              >
                {/* Square Toggle Block */}
                <div
                  style={{
                    width: 18,
                    height: 18,
                    backgroundColor: "#fff",
                    borderRadius: 50,
                    transition: "0.3s"
                  }}
                />

                {/* Name */}
                <span
                  style={{
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: 13,
                    minWidth: 60,
                    textAlign: "center",
                    // paddingLeft: 5,
                    // paddingRight: 5,
                  }}
                >
                  {isEnabled ? "Enabled" : "Disabled"}
                </span>
              </div>
            </div>

          );
        },
      }
      ,
      {
        field: "actions",
        headerName: "Action",
        width: 80,
        renderCell: (params) => (
          <Box display="flex" gap={0} sx={{ p: 0, m: 0, minHeight: 0 }}>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                sx={{ p: 0, m: 0, minWidth: 24, minHeight: 24, cursor: "pointer" }}
                onClick={() => handleEmployeeUpdate(params.row)}
              >
                <Edit fontSize="small" sx={{ fontSize: 15, cursor: "pointer" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                color="error"
                size="small"
                sx={{ p: 0, m: 0, minWidth: 24, minHeight: 24, cursor: "pointer" }}
                onClick={() => handleDeleteConfirmation(params.row.id)}
              >
                <Delete fontSize="small" sx={{ fontSize: 15, cursor: "pointer" }} />
              </IconButton>
            </Tooltip>
          </Box>
        )
      }
    ] : []),
  ];

  const filteredRows = rows.filter(row => {
    const query = searchText.toLowerCase();
    return (
      row.name?.toLowerCase().includes(query) ||
      row.email?.toLowerCase().includes(query) ||
      row.phone?.toLowerCase().includes(query) ||
      row.regionName?.toLowerCase().includes(query) ||
      row.pincodeRange?.toString().includes(query) ||
      row.fullAddress?.toString().includes(query) ||
      row.aadhaarNumber?.toString().includes(query)
    );
  });
  const getExportData = () => getSelectedExportRows(filteredRows, selectedRowIds);

  const handleExportCSV = () => {
    const exportRows = getExportData();
    if (!exportRows) return;
    const csvContent = exportRows.map((row, index) => [index + 1,
    row.createdAt ? dayjs(row.createdAt).format("DD MMM YYYY HH:mm") : "----", row.name, row.email, row.phone, row.fullAddress, row.aadhaarNumber, row.regionName, row.pincodeRange, row.enabled
    ].join(',')).join('\n');
    const headers = ['S.No.', 'Created Date', 'Name', 'Email', 'Phone', 'Address', 'Aadhaar Number', 'PinCode Area', 'PinCode Range', "Enable"].join(',');
    const csv = `${headers}\n${csvContent}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'employees.csv';
    link.click();
  };
  const handleExportPDF = () => {
    const exportRows = getExportData();
    if (!exportRows) return;
    const doc = new jsPDF();
    doc.setFontSize(12);
    doc.text("Employee List", 14, 15);
    const tableColumn = ['S.No.', 'Created Date', 'Name', 'Email', 'Phone', 'Address', 'Aadhaar Number', 'PinCode Area', 'PinCode Range', "Enable"];
    const tableRows = exportRows.map((row, index) => [index + 1, row.createdAt ? dayjs(row.createdAt).format("DD MMM YYYY HH:mm") : "----", row.name, row.email, row.phone, row.fullAddress, row.aadhaarNumber, row.regionName, row.pincodeRange, row.enabled]);
    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      startY: 20,
      theme: 'striped',
      headStyles: {
        fillColor: [37, 99, 235], // Tailwind blue-600 (RGB: 37,99,235)
        textColor: [255, 255, 255], // white text
        halign: 'left',
      },
      styles: { fontSize: 8, cellPadding: 1, overflow: 'linebreak' },
    });
    doc.save('employees.pdf');
  };
  const handlePrint = () => {
    const exportRows = getExportData();
    if (!exportRows) return;
    const printWindow = window.open('', '_blank');
    const headers = ['S.No.', 'Created Date', 'Name', 'Email', 'Phone', 'Address', 'Aadhaar Number', 'PinCode Area', 'PinCode Range', "Enable"];
    const tableContent = exportRows.map((row, index) => [index + 1, row.createdAt ? dayjs(row.createdAt).format("DD MMM YYYY HH:mm") : "----", row.name, row.email, row.phone, row.fullAddress, row.aadhaarNumber, row.regionName, row.pincodeRange, row.enabled]);
    const html = `
    <html>
      <head>
        <title>Employee List</title>
        <style>
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid black; padding: 8px; text-align: left; }
          th {background: #449AEF;
                color: white;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact; }
          tr:nth-child(even) { background-color: #f5f5f5; }
        </style>
      </head>
      <body>
        <h2>Employee List</h2>
        <table>
          <thead>
            <tr>
              ${headers.map(header => `<th>${header}</th>`).join('')}
            </tr>
          </thead>
          <tbody>
            ${tableContent.map(row => `
              <tr>
                ${row.map(cell => `<td>${cell}</td>`).join('')}
              </tr>
            `).join('')}
          </tbody>
        </table>
      </body>
    </html>
  `;
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.print();
  };

  const handleOpenModal = () => {
    setEmployeeData({ name: '', email: '', phone: '', password: '' });
    setSelectedMasterPinCodeId(0);
    setEditingId(null);
    setOpenModal(true);
    handleCancle();
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    handleCancle();
  };

  return (
    <Box >
      <Card className="p-4" sx={{ borderRadius: 2, boxShadow: 1, }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <CardHeader
            title={<Typography variant="h6" className="text-gray-700">EMPLOYEES</Typography>}
            sx={{ p: 0 }}
          />
          {writeAction && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                sx={{
                  background: "linear-gradient(90deg, #449AEF 0%, #f87171 100%)",
                  color: "#fff",
                  boxShadow: "0 2px 8px rgba(239,68,68,0.15)",
                  transition: "all 0.2s",
                  "&:hover": {
                    background: "linear-gradient(90deg, #f87171 0%, #449AEF 100%)",
                    boxShadow: "0 4px 16px rgba(239,68,68,0.25)",
                    transform: "scale(1.02)",
                  },
                  cursor: "pointer",
                  p: 1,
                  borderRadius: '50%',
                }}
                onClick={handleOpenModal}
              >
                <AddIcon />
              </IconButton>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, color: "#449AEF", fontSize: "1.2rem", letterSpacing: 0.2 }}
              >
                Add Employee
              </Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1.5, mb: { xs: 2, sm: 0 } }}>
            <Button
              variant="contained"
              startIcon={<FileDownload />}
              onClick={handleExportCSV}
              sx={{
                background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.85rem',
                borderRadius: '6px',
                px: 2,
                py: 0.8,
                transition: 'all 0.2s',
                '&:hover': {
                  background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                  boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                  transform: 'scale(1.02)',
                },
                cursor: 'pointer',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Export CSV
            </Button>
            <Button
              variant="contained"
              startIcon={<PictureAsPdf />}
              onClick={handleExportPDF}
              sx={{
                background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.85rem',
                borderRadius: '6px',
                px: 2,
                py: 0.8,
                transition: 'all 0.2s',
                '&:hover': {
                  background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                  boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                  transform: 'scale(1.02)',
                },
                cursor: 'pointer',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Export PDF
            </Button>
            <Button
              variant="contained"
              startIcon={<Print />}
              onClick={handlePrint}
              sx={{
                background: 'linear-gradient(90deg, #592545 0%, #a63f7b 100%)',
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.85rem',
                borderRadius: '6px',
                px: 2,
                py: 0.8,
                transition: 'all 0.2s',
                '&:hover': {
                  background: 'linear-gradient(90deg, #a63f7b 0%, #592545 100%)',
                  boxShadow: '0 4px 12px rgba(89,37,69,0.2)',
                  transform: 'scale(1.02)',
                },
                cursor: 'pointer',
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Print
            </Button>
          </Box>
          <Box>
            <TextField
              label="Search Employees"
              variant="outlined"
              size="small"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              sx={{ width: { xs: '100%', sm: '300px' } }}
            />
          </Box>
        </Box>
        <Box sx={{ height: "100%", width: "100%" }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            pagination
            initialState={{
              pagination: { paginationModel: { pageSize: 10, page: 0 } },
            }}
            pageSizeOptions={[10, 25, 50, 75, 100]}
            getRowId={row => row.id}
            checkboxSelection
            onRowSelectionModelChange={(ids) => setSelectedRowIds(ids)}
            rowSelectionModel={selectedRowIds}
            disableSelectionOnClick
            density="compact"
            experimentalFeatures={{ virtualization: true }}
            loading={employees.loading}
            autoHeight={true}
            sx={{
              borderRadius: 2,
              fontSize: "0.8rem",
              "& .MuiDataGrid-cell": {
                fontSize: "0.8rem",
                border: "1px solid rgb(204, 201, 201)",
                minHeight: "36px !important",
                height: "36px !important",
                lineHeight: "1.2",
                paddingTop: "4px",
                paddingBottom: "4px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              },
              "& .MuiDataGrid-columnHeader": {
                backgroundColor: "#449AEF",
                color: "#fff",
                fontWeight: "bold",
                fontSize: "0.8rem",
                border: "1px solid rgb(204, 201, 201)",
                minHeight: "36px !important",
                height: "36px !important",
                lineHeight: "1.2",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontSize: "0.8rem",
                fontWeight: 600,
              },
              "& .MuiDataGrid-row": {
                maxHeight: "36px !important",
                minHeight: "36px !important",
              },
              "& .MuiDataGrid-row:nth-of-type(odd)": {
                backgroundColor: "#f5f5f5",
              },
              "& .MuiDataGrid-row:nth-of-type(even)": {
                backgroundColor: "#ffffff",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          />
        </Box>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmId !== null}
        onClose={() => setDeleteConfirmId(null)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>Are you sure you want to delete this employee?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* employee add and update form render */}
      <Dialog open={openModal} onClose={handleCloseModal} fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
          },
        }}>
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '1.25rem',
            fontWeight: 'bold',
            mt: 1,
            mb: 0.5,
            pb: 1,
            borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
          }}
        >
          {editingId ? "UPDATE EMPLOYEE" : "ADD NEW EMPLOYEE"}
          <IconButton onClick={handleCloseModal} size="small" sx={{ ml: 2 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 2, py: 1 }}>
          <Card className="mb-4 mt-4" sx={{ borderRadius: 2, boxShadow: 1 }}>
            <CardContent sx={{ p: 2 }}>
              <form onSubmit={handleSubmit}>
                <Grid container spacing={2} sx={{ p: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Name"
                      name="name"
                      value={employeeData.name}
                      onChange={handleChange}
                      fullWidth
                      required
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Email"
                      name="email"
                      value={employeeData.email}
                      onChange={handleChange}
                      fullWidth
                      required
                      size="small"
                      autoComplete="email"
                      error={!!error}
                      helperText={error}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Phone"
                      name="phone"
                      value={employeeData.phone}
                      onChange={handleChange}
                      fullWidth
                      required
                      size="small"
                      inputProps={{ maxLength: 10 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={editingId ? "New Password" : "Password"}
                      name="password"
                      placeholder={editingId ? "Enter New Password" : "Enter Password"}
                      type={showPassword ? "text" : "password"}
                      value={employeeData.password}
                      onChange={handleChange}
                      fullWidth
                      required={!editingId}
                      size="small"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                            >
                              {showPassword ? (
                                <EyeSlashIcon className="h-5 w-5" />
                              ) : (
                                <EyeIcon className="h-5 w-5" />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={12}>
                    <TextField
                      required
                      id="fullAddress"
                      name="fullAddress"
                      label="Full Address"
                      fullWidth
                      multiline
                      rows={2}
                      value={employeeData.fullAddress}
                      onChange={handleChange}
                      autoComplete="given-fullAddress"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small" >
                      <InputLabel id="pincode-label">PinCode Area</InputLabel>
                      <Select
                        labelId="pincode-label"
                        value={selectedMasterPinCodeId}
                        onChange={(e) => setSelectedMasterPinCodeId(e.target.value)}
                        label="PinCode Area"
                        required
                      >
                        <MenuItem value={0} disabled>Select PinCode Area...</MenuItem>
                        {pincodeAreas?.pinCodes?.response?.map((area) => (
                          <MenuItem key={area.id} value={area.id}>
                            {`${area.regionName} (${area.startPincode} - ${area.endPincode})`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Aadhaar Number"
                      name="aadhaarNumber"
                      value={employeeData.aadhaarNumber}
                      onChange={handleChange}
                      fullWidth
                      required
                      size="small"
                      inputProps={{ maxLength: 12 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Button
                        variant="outlined"
                        component="label"
                        fullWidth
                        size="small"   // 👈 Add this
                        sx={{ py: 0.8, px: 2 }}  // 👈 Reduce padding
                        startIcon={<UploadFile />}
                      >
                        Upload Front Aadhaar
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          ref={fileInputRef}
                          onChange={handleFrontFileChange}
                        />
                      </Button>

                    </Box>
                    {frontError && <Typography color="error" variant="caption">{frontError}</Typography>}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Button
                        variant="outlined"
                        component="label"
                        fullWidth
                        size="small"   // 👈 Add this
                        sx={{ py: 0.8, px: 2 }}  // 👈 Reduce padding
                        startIcon={<UploadFile />}
                      >
                        Upload Back Aadhaar
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          ref={fileInputRef}
                          onChange={handleBackFileChange}
                        />
                      </Button>

                    </Box>
                    {backError && <Typography color="error" variant="caption">{backError}</Typography>}
                  </Grid>
                  {frontUploadedImage && (
                    <Grid item xs={3}>
                      <Typography variant="body2">Current Front:</Typography>
                      <img src={frontUploadedImage} alt="Uploaded" width="80" height="80" style={{ borderRadius: 8 }} />
                    </Grid>
                  )}
                  {frontPreview && (
                    <Grid item xs={3}>
                      <Typography variant="body2">Preview Front:</Typography>
                      <img src={frontPreview} alt="Uploaded" width="80" height="80" style={{ borderRadius: 8 }} />
                    </Grid>
                  )}
                  {backUploadedImage && (
                    <Grid item xs={3}>
                      <Typography variant="body2">Current Back:</Typography>
                      <img src={backUploadedImage} alt="Uploaded" width="80" height="80" style={{ borderRadius: 8 }} />
                    </Grid>
                  )}
                  {backPreview && (
                    <Grid item xs={3}>
                      <Typography variant="body2">Preview Back:</Typography>
                      <img src={backPreview} alt="Uploaded" width="80" height="80" style={{ borderRadius: 8 }} />
                    </Grid>
                  )}

                  <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", gap: 2, }}>
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={employees.loading}
                      sx={{
                        background: "linear-gradient(90deg, #449AEF 0%, #f87171 100%)",
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        borderRadius: "6px",
                        boxShadow: "0 2px 8px rgba(239,68,68,0.15)",
                        px: 2.5,
                        py: 0.8,
                        transition: "all 0.2s",
                        "&:hover": {
                          background: "linear-gradient(90deg, #f87171 0%, #449AEF 100%)",
                          boxShadow: "0 4px 16px rgba(239,68,68,0.25)",
                          transform: "scale(1.02)",
                        },
                        width: { xs: "100%", sm: "auto" },
                        textAlign: "center"
                      }}
                    >
                      {employees.loading ? (
                        <>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Processing...
                        </>
                      ) : (editingId ? "Update Employee" : "Add Employee")}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleCloseModal}
                      sx={{
                        color: "#449AEF",
                        borderColor: "#449AEF",
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        borderRadius: "6px",
                        px: 2.5,
                        py: 0.8,
                        transition: "all 0.2s",
                        "&:hover": {
                          backgroundColor: "#449AEF",
                          color: "#fff",
                          borderColor: "#449AEF",
                          boxShadow: "0 2px 8px rgba(239,68,68,0.1)",
                        },
                        width: { xs: "100%", sm: "auto" },
                        textAlign: "center"
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </CardContent>
          </Card>
        </DialogContent>
      </Dialog>
      {/* Common Popup */}
      <ImagePopup
        open={imageOpen}
        onClose={() => setImageOpen(false)}
        imageUrl={imageUrl}
      />
    </Box>
  );
};

export default AddEmployee;
